import { CommonModule } from '@angular/common';
import { Component, computed, inject, input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  DetailsPageContentPlaceholderComponent,
  DetailsPageHeaderComponent,
  DetailsPageHeaderPlaceholderComponent,
} from '@shared/components';
import { Breadcrumb } from '@shared/models';
import { transactionFeature, TransactionsActions } from '@store';

@Component({
  selector: 'app-view-transaction',
  imports: [
    DetailsPageHeaderPlaceholderComponent,
    DetailsPageContentPlaceholderComponent,
    DetailsPageHeaderComponent,
    CommonModule,
    RouterLink,
    RouterOutlet,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './view-transaction.component.html',
})
export class ViewTransactionComponent implements OnInit {
  store = inject(Store);

  router = inject(Router);

  transactionId = input<string>();

  businessAccountId = input<string>();

  financialAccountId = input<string>();

  pageReturnURL = computed(() => {
    if (this.financialAccountId()) {
      return `/app/financial-accounts/${this.financialAccountId()}/activities`;
    }
    if (this.businessAccountId()) {
      return `/app/business-accounts/${this.businessAccountId()}/transactions`;
    }
    return '/app/transactions';
  });

  breadcrumbs = computed((): Breadcrumb[] => {
    if (this.financialAccountId()) {
      return [
        { label: 'Financial Accounts', url: '/app/financial-accounts' },
        { label: 'Financial Account Details', url: `/app/financial-accounts/${this.financialAccountId()}` },
        { label: 'Activities', url: this.pageReturnURL() },
        { label: 'Transactions Details' },
      ];
    }
    if (this.businessAccountId()) {
      return [
        { label: 'Business Accounts', url: '/app/business-accounts' },
        { label: 'Business Account Details', url: `/app/business-accounts/${this.businessAccountId()}` },
        { label: 'Transactions', url: this.pageReturnURL() },
        { label: 'Transactions Details' },
      ];
    }
    return [{ label: 'Transactions', url: this.pageReturnURL() }, { label: 'Transactions Details' }];
  });

  tabBaseUrl = computed(() => {
    if (this.financialAccountId()) {
      return `/app/financial-accounts/${this.financialAccountId()}/activities/${this.transactionId()}`;
    }
    if (this.businessAccountId()) {
      return `/app/business-accounts/${this.businessAccountId()}/transactions/${this.transactionId()}`;
    }
    return `/app/transactions/${this.transactionId()}`;
  });

  tabItems = computed(() => {
    return [
      { tabName: 'Transaction Details', href: `${this.tabBaseUrl()}/details` },
      { tabName: 'Journal Activity', href: `${this.tabBaseUrl()}/journals` },
      { tabName: 'Ledger Entries', href: `${this.tabBaseUrl()}/ledger-entries` },
    ];
  });

  transaction$ = this.store.select(transactionFeature.selectTransactionDetails);

  ngOnInit(): void {
    this.store.dispatch(TransactionsActions.loadTransactionDetails());
  }

  isActive(tabHref: string): boolean {
    return this.router.isActive(tabHref, {
      paths: 'exact',
      queryParams: 'subset',
      fragment: 'ignored',
      matrixParams: 'ignored',
    });
  }
}
